/**
 * Beneficiaries directory (office staff). Search + village/block filter, paginated
 * table. Read-focused — beneficiaries are created through the intake wizard.
 */
import { useCallback, useEffect, useState } from 'react';
import { Search, RefreshCw, MapPin, ShieldCheck } from 'lucide-react';
import { api } from '../lib/apiClient.js';
import { formatDate } from '../features/cases/caseMeta.js';
import styles from './Beneficiaries.module.css';

export default function Beneficiaries() {
  const [q, setQ] = useState('');
  const [state, setState] = useState({ status: 'loading', items: [], nextCursor: null, hasMore: false });

  const load = useCallback(
    async (append = false) => {
      setState((s) => ({ ...s, status: append ? s.status : 'loading' }));
      try {
        const params = new URLSearchParams({ limit: '30' });
        if (q) params.set('q', q);
        if (append && state.nextCursor) params.set('cursor', state.nextCursor);
        const env = await api.getEnvelope(`/beneficiaries?${params}`);
        setState((s) => ({
          status: 'ready',
          items: append ? [...s.items, ...env.data] : env.data,
          nextCursor: env.meta.page.nextCursor,
          hasMore: env.meta.page.hasMore,
        }));
      } catch (err) {
        setState({ status: 'error', items: [], error: err.message });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [q],
  );

  useEffect(() => {
    const t = setTimeout(() => load(false), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Casework</p>
          <h1 className={styles.h1}>Beneficiaries</h1>
        </div>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={16} />
          <input placeholder="Search name, phone, village…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button className={styles.ghost} onClick={() => load(false)}>
          <RefreshCw size={15} />
        </button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Location</th>
              <th>Consent</th>
              <th>Added</th>
            </tr>
          </thead>
          <tbody>
            {state.status === 'loading' &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={5}>
                    <div className={styles.skeleton} />
                  </td>
                </tr>
              ))}
            {state.status === 'error' && (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  {state.error} — <button onClick={() => load(false)}>retry</button>
                </td>
              </tr>
            )}
            {state.status === 'ready' && state.items.length === 0 && (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  No beneficiaries match.
                </td>
              </tr>
            )}
            {state.status === 'ready' &&
              state.items.map((b) => (
                <tr key={b.id}>
                  <td>
                    <div className={styles.nameCell}>
                      <span className={styles.avatar}>{b.name.charAt(0)}</span>
                      <strong>{b.name}</strong>
                    </div>
                  </td>
                  <td className="mono">{b.phone}</td>
                  <td>
                    <span className={styles.loc}>
                      <MapPin size={13} /> {b.village}
                      {b.block ? `, ${b.block}` : ''}
                    </span>
                  </td>
                  <td>
                    {b.consent?.publicAfterClose ? (
                      <span className={styles.consent}>
                        <ShieldCheck size={13} /> Public on close
                      </span>
                    ) : (
                      <span className={styles.sub}>Private</span>
                    )}
                  </td>
                  <td className={styles.sub}>{formatDate(b.createdAt)}</td>
                </tr>
              ))}
          </tbody>
        </table>
        {state.hasMore && state.status === 'ready' && (
          <button className={styles.loadMore} onClick={() => load(true)}>
            Load more
          </button>
        )}
      </div>
    </div>
  );
}
